import { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supportedLanguages, loadLanguage } from '../i18n';

// Key to track if we've already done the initial language redirect
const LANGUAGE_REDIRECT_KEY = 'unitconvertor_lang_redirected';

// Detect browser's preferred language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  // Extract base language code (e.g., 'es-MX' -> 'es')
  const baseLang = browserLang.split('-')[0].toLowerCase();
  return baseLang;
};

const LanguageRouter = () => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleLanguageChange = async () => {
      const validLang = supportedLanguages.find(l => l.code === lang);

      if (lang && !validLang) {
        // Invalid language, redirect to English version
        const pathWithoutLang = window.location.pathname.replace(`/${lang}`, '') || '/';
        navigate(pathWithoutLang, { replace: true });
      } else if (lang && validLang) {
        // Load the language if needed and switch to it
        await loadLanguage(lang);
        i18n.changeLanguage(lang);
        // Mark that user has been on a localized page (so we don't redirect them away)
        localStorage.setItem(LANGUAGE_REDIRECT_KEY, 'true');
      } else {
        // No lang prefix = English route
        // Check if this is a first-time visitor who should be redirected
        const hasRedirected = localStorage.getItem(LANGUAGE_REDIRECT_KEY);
        const savedLang = localStorage.getItem('i18nextLng');

        if (!hasRedirected && !savedLang) {
          // First visit - detect browser language and redirect if supported
          const browserLang = detectBrowserLanguage();
          const supportedLang = supportedLanguages.find(l => l.code === browserLang);

          if (supportedLang && browserLang !== 'en') {
            // Redirect to localized version
            const currentPath = location.pathname;
            const newPath = `/${browserLang}${currentPath === '/' ? '' : currentPath}`;
            localStorage.setItem(LANGUAGE_REDIRECT_KEY, 'true');
            navigate(newPath, { replace: true });
            return;
          }
        }

        // Stay on English
        localStorage.setItem(LANGUAGE_REDIRECT_KEY, 'true');
        i18n.changeLanguage('en');
      }
    };

    handleLanguageChange();
  }, [lang, i18n, navigate, location.pathname]);

  return <Outlet />;
};

export default LanguageRouter;
